/**
 * indexHeatmapModel.ts
 * Item 5.3 — pure logic backing the Phase-2 "Index tests" daily utilization
 * heatmap: visible-horizon computation and the per-day cell view model.
 *
 * The day-by-day firm/soft split (I13) is BACKEND truth
 * (`indexCapacityService.getIndexCapacity`, Item 3.2) — this module never
 * recomputes that sum. It only:
 *
 *   1. Derives the visible date horizon from this source's own allocation
 *      windows (padded a few working days each side, min ~3 weeks visible).
 *   2. Splits the backend's `firmPct` for THIS scenario into two buckets the
 *      planner can actually act on — "own tentative" (this plan's still-draft
 *      rows) vs "committed" (everything else already locked in, from any
 *      scenario) — since a Scenario maps 1:1 to one source (Quote/WorkOrder),
 *      "this source's own allocations" IS "this scenario's own rows", so the
 *      own-tentative contribution can be re-derived client-side with the same
 *      per-day spread math already mirrored in `indexRowMath.ts` without a
 *      backend round-trip. `committed = firmPct − ownTentativePct` (clamped to
 *      ≥0) therefore always reconciles back to the backend's firmPct.
 *   3. Builds the tooltip's plain-text lines so they're unit-testable without
 *      mounting the component tree.
 *
 * ⚠️ Presentation-only optimism (same caveat as indexRowMath.ts): the
 * `firmPct`/`softPct`/`band`/`isOverCapacity`/`byType` fields are NEVER
 * recomputed here — they come straight from the backend response.
 */

import {
  evenSpreadPerDay,
  INDEX_UNKNOWN_CAPACITY_FALLBACK,
} from './indexRowMath';
import type { DayBaselineGhost } from './indexBaselineDiff';
import type {
  IndexAllocationStatus,
  IndexCapacityBand,
  IndexCapacityTypeInfo,
  IndexDayCapacity,
  IndexDayTypeBreakdown,
} from '../../../../../services/scheduleIndex';

// ---------------------------------------------------------------------------
// Horizon computation
// ---------------------------------------------------------------------------

/** A single allocation's planning window — the shape the horizon needs. */
export interface HeatmapWindowInput {
  StartDate: string;
  EndDate: string;
}

/** Calendar-day padding applied to each side of the union of allocation windows. */
export const HEATMAP_PAD_CALENDAR_DAYS = 5;
/** The visible horizon is never narrower than this many calendar days (~3 weeks). */
export const HEATMAP_MIN_VISIBLE_DAYS = 21;

function addCalendarDays(dateIso: string, days: number): string {
  const d = new Date(`${dateIso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Inclusive calendar-day span between two ISO dates (start === end → 1). */
function calendarDaySpan(startIso: string, endIso: string): number {
  const start = Date.parse(`${startIso}T00:00:00Z`);
  const end = Date.parse(`${endIso}T00:00:00Z`);
  return Math.round((end - start) / 86_400_000) + 1;
}

/**
 * Compute the heatmap's visible [start, end] horizon: the union of the given
 * allocation windows, padded `HEATMAP_PAD_CALENDAR_DAYS` calendar days on each
 * side, then widened forward (if needed) so the total span is never under
 * `HEATMAP_MIN_VISIBLE_DAYS`. Falls back to `todayIso` (no windows yet — a
 * stale/direct-nav edge case) so the grid still renders something sensible.
 *
 * Calendar-day (not working-day) padding mirrors the existing
 * `Phase1MachineGrid.computeWindow` precedent in this codebase — holiday-aware
 * padding would need a holiday fetch BEFORE the window it's padding exists, a
 * chicken/egg the existing pattern already accepts as an approximation.
 */
export function computeHeatmapHorizon(
  windows: readonly HeatmapWindowInput[],
  todayIso: string,
): { start: string; end: string } {
  const starts = windows.map((w) => w.StartDate).filter(Boolean).sort();
  const ends = windows.map((w) => w.EndDate).filter(Boolean).sort();
  const rawStart = starts[0] ?? todayIso;
  const rawEnd = ends[ends.length - 1] ?? todayIso;

  const start = addCalendarDays(rawStart, -HEATMAP_PAD_CALENDAR_DAYS);
  let end = addCalendarDays(rawEnd, HEATMAP_PAD_CALENDAR_DAYS);

  const span = calendarDaySpan(start, end);
  if (span < HEATMAP_MIN_VISIBLE_DAYS) {
    end = addCalendarDays(end, HEATMAP_MIN_VISIBLE_DAYS - span);
  }
  return { start, end };
}

// ---------------------------------------------------------------------------
// Own-scenario contribution split (I13)
// ---------------------------------------------------------------------------

/** The subset of `IndexAllocation` fields the own-contribution split needs. */
export interface OwnAllocationInput {
  TaskTypeKey: string;
  StartDate: string;
  EndDate: string;
  TestCount: number;
  IndexTestsPerDay?: number | null;
  Status: IndexAllocationStatus;
}

/**
 * Σ this source's own Tentative-status allocations' contribution to `date`,
 * as a percent of the shared Index machine (same
 * `perDayCount ÷ capacity × 100` formula the backend sums over — see
 * `indexCapacity.ts`'s `dayUtilizationPct`). Committed-status rows are
 * intentionally excluded here — once submitted they're no longer "this
 * plan's still-draft work", they fold into the generic `committed` bucket.
 */
function ownTentativeContributionPct(
  ownAllocations: readonly OwnAllocationInput[],
  date: string,
  isWorkingDayThisDate: boolean,
  holidayDates: readonly string[],
): number {
  if (!isWorkingDayThisDate) return 0;
  let sum = 0;
  for (const a of ownAllocations) {
    if (a.Status !== 'Tentative') continue;
    if (date < a.StartDate || date > a.EndDate) continue;
    const spread = evenSpreadPerDay(a.TestCount, a.StartDate, a.EndDate, holidayDates);
    if (!spread.ok) continue; // defensive — persisted rows were validated at write time (I22)
    const capacity =
      a.IndexTestsPerDay != null && a.IndexTestsPerDay > 0
        ? a.IndexTestsPerDay
        : INDEX_UNKNOWN_CAPACITY_FALLBACK;
    sum += (spread.perDayCount / capacity) * 100;
  }
  return Math.round(sum * 10) / 10;
}

// ---------------------------------------------------------------------------
// Per-day cell view model
// ---------------------------------------------------------------------------

export interface IndexDayCellModel {
  date: string;
  isWorkingDay: boolean;
  /** Backend truth — Committed (any scenario) + own Tentative (I13). */
  firmPct: number;
  /** Backend truth — other drafts' Tentative only, advisory (I13). */
  softPct: number;
  band: IndexCapacityBand;
  isOverCapacity: boolean;
  hasUnknownCapacity: boolean;
  /** This plan's own still-draft (Tentative) contribution — a subset of firmPct. */
  ownTentativePct: number;
  /** Everything else already committed (any scenario) — firmPct − ownTentativePct, clamped ≥0. */
  committedPct: number;
  byType: readonly IndexDayTypeBreakdown[];
}

// (buildIndexDayCellModels — the single summed-row model — was retired by the
// P3 per-type rework, 2026-07-29. `buildIndexTypeRows` below is its successor.)

// ---------------------------------------------------------------------------
// Per-type rows (P3, 2026-07-29 — "each index test is treated like a machine")
// ---------------------------------------------------------------------------

/**
 * One test type's heatmap row: its own capacity, its own cells, its own bands.
 * Reuses `IndexDayCellModel` per cell so `IndexCapacityHeatmapCell` renders a
 * per-type cell identically to the old summed cell — the numbers are simply
 * that type's own % of its own capacity (backend truth from `byType`).
 */
export interface IndexTypeRow {
  taskTypeKey: string;
  taskTypeName: string;
  capacity: number | null;
  cells: IndexDayCellModel[];
}

/**
 * Σ this source's own Tentative allocations OF ONE TYPE on `date`, as a % of
 * that type's own capacity — the per-type analogue of
 * `ownTentativeContributionPct` (same spread math, filtered to the type).
 */
function ownTentativeTypeContributionPct(
  ownAllocations: readonly OwnAllocationInput[],
  taskTypeKey: string,
  date: string,
  isWorkingDayThisDate: boolean,
  holidayDates: readonly string[],
): number {
  return ownTentativeContributionPct(
    ownAllocations.filter((a) => a.TaskTypeKey === taskTypeKey),
    date,
    isWorkingDayThisDate,
    holidayDates,
  );
}

/**
 * Build the per-type heatmap rows from the capacity response (P3). Every type
 * in `types` gets a row spanning every day — days the type is idle render as
 * 0% cells rather than gaps, so the grid stays rectangular.
 */
export function buildIndexTypeRows(
  types: readonly IndexCapacityTypeInfo[],
  days: readonly IndexDayCapacity[],
  ownAllocations: readonly OwnAllocationInput[],
  holidayDates: readonly string[],
): IndexTypeRow[] {
  return types.map((t) => ({
    taskTypeKey: t.taskTypeKey,
    taskTypeName: t.taskTypeName,
    capacity: t.capacity,
    cells: days.map((d) => {
      const entry = d.byType.find((b) => b.taskTypeKey === t.taskTypeKey) ?? null;
      const firmPct = entry?.firmPct ?? 0;
      const ownTentativePct = ownTentativeTypeContributionPct(
        ownAllocations,
        t.taskTypeKey,
        d.date,
        d.isWorkingDay,
        holidayDates,
      );
      return {
        date: d.date,
        isWorkingDay: d.isWorkingDay,
        firmPct,
        softPct: entry?.softPct ?? 0,
        band: entry?.band ?? 'low',
        isOverCapacity: entry?.isOverCapacity ?? false,
        hasUnknownCapacity: entry?.hasUnknownCapacity ?? false,
        ownTentativePct,
        committedPct: Math.max(0, Math.round((firmPct - ownTentativePct) * 10) / 10),
        byType: entry ? [entry] : [],
      };
    }),
  }));
}

// ---------------------------------------------------------------------------
// Tooltip content (pure — testable without mounting the component tree)
// ---------------------------------------------------------------------------

/**
 * Plain-text lines for one day cell's tooltip/popover breakdown (I13).
 * `ghost` (Item 5.4, Compare-to-Baseline, I16) is optional — when supplied
 * (compareToBaseline ON + a still-editable draft, see
 * `isCompareToBaselineEligible`), two extra lines report the BASELINE
 * perspective's firmPct + this draft's delta from it.
 */
export function buildDayTooltipLines(
  model: IndexDayCellModel,
  ghost?: DayBaselineGhost | null,
): string[] {
  if (!model.isWorkingDay) {
    return ['Non-working day (weekend or stat holiday) — no index capacity consumed.'];
  }

  const lines: string[] = [
    `Firm total: ${model.firmPct}%${model.isOverCapacity ? ' — OVER CAPACITY' : ''}`,
    `Own tentative (this plan): ${model.ownTentativePct}%`,
    `Committed (any scenario): ${model.committedPct}%`,
    `Soft — other drafts' tentative: ${model.softPct}%`,
  ];

  if (model.hasUnknownCapacity) {
    lines.push('* one or more types used the default 8/day capacity (not yet set by admin)');
  }

  if (model.byType.length > 0) {
    lines.push('By test type:');
    for (const t of model.byType) {
      const cap = t.capacity != null ? `${t.capacity}/day` : '8/day *';
      lines.push(`  ${t.taskTypeName}: ${t.firmCount} firm + ${t.softCount} soft (cap ${cap})`);
    }
  }

  if (ghost) {
    lines.push(
      `Baseline (no draft): ${ghost.baselineFirmPct}%${ghost.baselineIsOverCapacity ? ' — OVER CAPACITY' : ''}`,
    );
    lines.push(`Δ from baseline: ${ghost.deltaPct >= 0 ? '+' : ''}${ghost.deltaPct}%`);
  }

  return lines;
}
